/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { MissionRoleType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequesterSummaryProps {
  id: string;
  missionRole: MissionRoleType;
  vetNumber: string | null;
}

export class RequesterSummaryRecord extends Record<RequesterSummaryProps>({
  id: null as any,
  missionRole: null as any,
  vetNumber: null as any,
}) {
  public static fromJS(json: {
    id: string;
    missionRole: MissionRoleType;
    vetNumber: string | null;
  }): RequesterSummaryRecord {
    if (json == null) {
      throw new Error('RequesterSummaryRecord.fromJS: parameter cannot be null');
    }
    return new RequesterSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequesterSummaryJson.id',
      ),
      missionRole: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'missionRole', MissionRoleType.FullService),
        'RequesterSummaryJson.missionRole',
      ),
      vetNumber: json.vetNumber,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    missionRole: MissionRoleType;
    vetNumber: string | null;
  }): [RequesterSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('RequesterSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequesterSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequesterSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      missionRole: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'missionRole', MissionRoleType.FullService),
            'RequesterSummaryJson.missionRole',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vetNumber: tryOrDefault(
        () => json.vetNumber,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: RequesterSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('RequesterSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequesterSummaryViewModelBase implements RequesterSummaryProps {
  protected readonly _props: RequesterSummaryProps;
  private _json?: RequesterSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get missionRole(): MissionRoleType {
    return this._props.missionRole;
  }
  get vetNumber(): string | null {
    return this._props.vetNumber;
  }

  public get isANMC() {
    return this._props.missionRole === MissionRoleType.ANMC;
  }
  public get isCharter() {
    return this._props.missionRole === MissionRoleType.Charter;
  }
  public get isFlightFollowing() {
    return this._props.missionRole === MissionRoleType.FlightFollowing;
  }
  public get isFullService() {
    return this._props.missionRole === MissionRoleType.FullService;
  }
  public get isHospice() {
    return this._props.missionRole === MissionRoleType.Hospice;
  }
  public get isMassCasualty() {
    return this._props.missionRole === MissionRoleType.MassCasualty;
  }
  public get isPrecautionaryEvacuation() {
    return this._props.missionRole === MissionRoleType.PrecautionaryEvacuation;
  }
  public get isQuote() {
    return this._props.missionRole === MissionRoleType.Quote;
  }
  public get isQuoteOrNonEmergent() {
    return this._props.missionRole === MissionRoleType.QuoteOrNonEmergent;
  }
  public get isSEARHC() {
    return this._props.missionRole === MissionRoleType.SEARHC;
  }
  public get isSupplemental() {
    return this._props.missionRole === MissionRoleType.Supplemental;
  }
  public get isTeamFlight() {
    return this._props.missionRole === MissionRoleType.TeamFlight;
  }
  public get isVetRide() {
    return this._props.missionRole === MissionRoleType.VetRide;
  }

  public isAnyMissionRole(...missionRoleType: MissionRoleType[]) {
    return missionRoleType.includes(this._props.missionRole);
  }

  constructor(props: RequesterSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        missionRole: this._props.missionRole,
        vetNumber: this._props.vetNumber,
      };
    }
    return this._json;
  }
}
